import { CoreTextView } from '@core/widgets';
import { useBehavior, useSkipFirstRender, useTriggerAction } from '@libs/core';
import { ActionActionTypeEnum, TWAction, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { useEffect, useEffectEvent, useMemo, useState } from 'react';
import Dash from '../../../common/Dash';
import { getSemanticColors, SemanticColor } from '../../../theme/colors';
import { TCardBalanceData, TCardBalanceDataMessages } from '../../../types/data';
import { ECardBalanceStatus } from '../../../types/enums';
import { TCardBalanceOptions } from '../../../types/options';
import { TextStyle } from '../../../types/styles';
import { getInlineStyles } from '../../../utils/styles';
import { getBackgroundHref } from '../../Image/imageUtils';
import { CardBottomButtons } from './CardBottomButtons';
import { PaysendCard } from './PaysendCard';
import styles from './paysendCard.module.css';
import { CardBalanceActionName } from './utils/utils';

const getActionByType = (actions: TWAction[] | undefined, actionType: string | ActionActionTypeEnum) => actions?.find((f) => f.actionType === actionType);

export const CardBalance = ({ widget }: TWidgetProps<TCardBalanceData, TCardBalanceOptions>) => {
    const { triggerAction, triggerActionsByType } = useTriggerAction(widget);
    const { isHidden } = useBehavior(widget);
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const messages = data.messages ?? ({} as TCardBalanceDataMessages);
    const status = data.status;
    const isExpired = status ? status === ECardBalanceStatus.EXPIRED : (data.expired ?? false);
    const isFrozen = status ? status === ECardBalanceStatus.FROZEN : (data.frozen ?? false);
    const suspiciousActivity = status ? status === ECardBalanceStatus.SUSPICIOUS_ACTIVITY : (data.suspiciousActivity ?? false);
    const isKycInProgress = status === ECardBalanceStatus.KYC_IN_PROGRESS;
    const cardIssued = status ? !isKycInProgress && status !== ECardBalanceStatus.NOT_ISSUED : (data.isCardIssued ?? false);
    const appleWalletAdded = data.appleWalletAdded ?? false;
    const googleWalletAdded = data.googleWalletAdded ?? false;
    const metaText = messages.metaText;
    const topLayoutStyles = getInlineStyles({
        variables: options.backgroundImage ? { 'top-layout-background': `url(${getBackgroundHref(options.backgroundImage)})` } : undefined,
    });

    const [isCardScreen, setCardScreen] = useState(false);
    const firstRender = useSkipFirstRender(isCardScreen);
    const actionAutoExecute = useMemo(() => getActionByType(widget.actions, CardBalanceActionName.autoExecute), [widget.actions]);
    const onTriggerAction = useEffectEvent((action: TWAction) => {
        return triggerAction(action);
    });

    useEffect(() => {
        if (actionAutoExecute) {
            onTriggerAction(actionAutoExecute).catch(console.error);
        }
    }, [actionAutoExecute]);

    const onCardClick = (isCardIssued: boolean, isActionAutoExecute: boolean) => {
        if (isCardIssued) {
            setCardScreen(true);
        } else if (!isActionAutoExecute) {
            triggerActionsByType(CardBalanceActionName.getCard, { isShowLoader: true }).catch(console.error);
        }
    };

    if (isHidden) {
        return false;
    }

    return (
        <>
            {!isCardScreen && (
                <div
                    data-test={widget.uid}
                    className={styles.paysendCardContainer}
                    style={getInlineStyles({ margin: options.margin })}
                    onClick={() => onCardClick(cardIssued, !!actionAutoExecute)}
                >
                    <PaysendCard
                        version={widget.version}
                        isFirstRender={firstRender}
                        isFrozen={isFrozen || suspiciousActivity}
                        isExpired={isExpired}
                        isKycInProgress={isKycInProgress}
                        metaText={metaText}
                        appleWalletAdded={appleWalletAdded}
                        googleWalletAdded={googleWalletAdded}
                        isCardIssued={cardIssued}
                        getCard={messages.getCard}
                    />
                </div>
            )}
            <div className={clsx(styles.cardScreen, isCardScreen && styles.cardScreenOnCardScreen)} data-test={'card-details-' + widget.uid}>
                <div className={styles.layoutContainer}>
                    <div className={clsx(styles.topLayout, isCardScreen && styles.topLayoutOnCardScreen)} style={topLayoutStyles}>
                        <div className={styles.layoutInner}>
                            <PaysendCard
                                version={widget.version}
                                isCardScreen
                                isFrozen={isFrozen || suspiciousActivity}
                                isExpired={isExpired}
                                metaText={metaText}
                                appleWalletAdded={appleWalletAdded}
                                googleWalletAdded={googleWalletAdded}
                                isCardIssued={cardIssued}
                                getCard={messages.getCard}
                            />
                            <div className={styles.textContainer}>
                                <CoreTextView
                                    text={messages.cardDescription}
                                    styleProps={{
                                        ...TextStyle.main,
                                        main: getSemanticColors(widget.version)[SemanticColor.TextPrimaryOnTopLayout],
                                        textAlign: 'center',
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={clsx(styles.bottomLayout, isCardScreen && styles.bottomLayoutCardScreen)}>
                        <Dash onTouchEnd={() => setCardScreen(false)} />
                        <div className={styles.layoutInner}>
                            <CardBottomButtons
                                widget={widget}
                                suspiciousActivity={suspiciousActivity}
                                isExpired={isExpired}
                                isFrozen={isFrozen}
                                messages={messages}
                                closeScreen={() => setCardScreen(false)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
